import { AccountTypes } from '@interfaces/models/account-types';
import { IndividualDesignationListedTypes } from '@interfaces/models/individual-designation-types';
import { IReceipt, ReceiptRow } from '@interfaces/models/receipt.interface';
import {
  SettlementFundAoAEnum,
  SettlementFundMap,
} from '@interfaces/settlement-fund/settlement-fund.enum';
import {
  FlowTypes,
  IRATypes,
  ISelectedAccount,
  ISelectedClientAccountsState,
  TransactionTypesEnum,
  urlMapper,
} from '@shared/models';
import { ReceiptModal } from '@shared/models/receipt-modal';
import { ReviewContent } from '@shared/services/content/content-jsons/review-content';
import { TransactionState } from '@store/reducers';
import { EstateState } from '@store/reducers/estate/estate.reducer';
import { IOpenAccountState } from '@store/reducers/open-account/open-account.reducer';
import { TriageState } from '@store/reducers/triage/triage.reducer';
import { IPersonalInfoClientState } from 'vg-interaction-model/dist/lib/features/personal-info-client/store/reducers/personal-info-client.reducer';

import { splitClientId } from './new-account/new-account.cashier';

export const IRATransferToType = {
  [IRATypes.OTHER]: 'Inherited IRA Estate Account',
  [IRATypes.ROTH]: 'Inherited Roth IRA Estate Account',
  [IRATypes.INHERITED_OTHER]: 'Inherited IRA Estate Account',
  [IRATypes.INHERITED_ROTH]: 'Inherited Roth IRA Estate Account',
};

export function getMultiInheritanceAccountToType(
  accountType: AccountTypes,
  iraType: IRATypes = IRATypes.UNKNOWN,
): string {
  const accountTypeMap = {
    [AccountTypes.INDIVIDUAL]: 'Individual',
    [AccountTypes.INDV]: 'Individual',
    [AccountTypes.JOINT]: 'Individual',
    [AccountTypes.JOINT_COMMUNITY_PROPERTY]: 'Individual',
    [AccountTypes.JOINT_TENANTS_WROS]: 'Individual',
  };

  const iraTypeMap = {
    [IRATypes.OTHER]: 'Traditional IRA',
    [IRATypes.ROTH]: 'Roth IRA',
    [IRATypes.INHERITED_OTHER]: 'Inherited IRA',
    [IRATypes.INHERITED_ROTH]: 'Inherited Roth IRA',
    [IRATypes.UNKNOWN]: 'Unknown',
  };

  return (
    accountTypeMap?.[accountType] ??
    (accountType === AccountTypes.IRA ? iraTypeMap[iraType] : 'Unknown')
  );
}

export function joinWithSpace(values: String[]): string {
  return values.filter(Boolean).join(' ');
}

export function formatPhoneNumber(number: string): string {
  if (number.length === 10) {
    return `${number.substring(0, 3)}-${number.substring(3, 6)}-${number.substring(6, 10)}`;
  }
  return number;
}

export function formatSSN(ssn: string): string {
  if (ssn.length === 9) {
    return `${ssn.substring(0, 3)}-${ssn.substring(3, 5)}-${ssn.substring(5, 9)}`;
  }
  return ssn;
}

export function toSentenceLowerCase(string: string) {
  if (!string) return '';
  const str = string.replace(/_/g, ' ');
  return str.charAt(0) + str.substring(1).toLowerCase();
}

export function buildAccountFromNonretirement(
  accountsFrom: ISelectedAccount[],
  accountType: string,
  editable: boolean,
  triageState: TriageState,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
): IReceipt {
  const flow = urlMapper[triageState.flowType];
  const receipt: IReceipt = {
    header: content?.transferringFromLabel || 'Transferring from',
    editable: AccountTypes.INDV === accountType ? false : editable,
    editRoute: `secure/${flow}/transfer-from`,
    sectionEvidenceID: 'accountFrom',
    rows: [],
  };
  let fromAccountType = 'fromAccountType';
  let fromAccountRegistration = 'fromAccountRegistration';
  let fromAccountNumber = 'fromAccountNumber';
  if (AccountTypes.INDV === accountType) {
    receipt.rows.push({
      label: content?.accountTypeLabel || 'Account type',
      values: ['Individual account'],
      evidenceID: fromAccountType,
    });
  }
  let counter = 0;
  const accountTitle = AccountTypes.UGMA_UTMA === accountType ? 'UGMA/UTMA' : 'Joint';
  accountsFrom.forEach((account) => {
    fromAccountType = fromAccountType.concat(counter.toString());
    fromAccountRegistration = fromAccountRegistration.concat(counter.toString());
    fromAccountNumber = fromAccountNumber.concat(counter.toString());
    receipt.rows.push(
      {
        label: content?.accountTypeLabel || 'Account type',
        values: [`${accountTitle} account`],
        evidenceID: fromAccountType,
      },
      {
        label: content?.accountRegistrationLabel || 'Account registration',
        values: [accountName(account)],
        evidenceID: fromAccountRegistration,
      },
      {
        label: content?.accountNumberLabel || 'Account number',
        values: [account.accountNumber],
        evidenceID: fromAccountNumber,
      },
    );
    if (account.accountData.nickname) {
      receipt.rows.push({
        label: 'Account nickname',
        values: [account.accountData.nickname],
        evidenceID: 'fromAccountNickname',
      });
    }
    counter++;
  });
  if (AccountTypes.UGMA_UTMA !== accountType) {
    receipt.rows.push({
      label: 'Source of Funds',
      values: ['Family/Inheritance'],
      evidenceID: 'sourceOfFunds',
    });
  }
  return receipt;
}

export function buildToAccountRow(
  personalInfo: IPersonalInfoClientState['clientData'],
  selectedAccounts: ISelectedClientAccountsState,
  openAccount: IOpenAccountState,
  settlementFund: SettlementFundAoAEnum,
  flowType: FlowTypes,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
) {
  const toAccountRows: ReceiptRow[] = [];
  const toAccount = selectedAccounts.transferTo.selectedAccount;

  const name = joinWithSpace([
    personalInfo.firstName,
    personalInfo.middleName,
    personalInfo.lastName,
  ]);
  if (
    TransactionTypesEnum.EXISTING === selectedAccounts.transactionType &&
    toAccount?.accountData
  ) {
    const toAccountDetail = toAccount.accountData;
    const accountName = toAccountDetail.accountName;
    // Do not remove this elvis operator. It breaks the selector without it
    const isBrokerage = toAccountDetail.productType?.includes('BROKERAGE');
    toAccountRows.push(
      {
        label: content?.accountTypeLabel || 'Account type',
        values: [toSentenceLowerCase(toAccountDetail.accountType)],
        evidenceID: 'toAccountType',
      },
      {
        label: content?.accountRegistrationLabel || 'Account registration',
        values: [isBrokerage ? accountName.substr(0, accountName.indexOf('—')) : accountName],
        evidenceID: 'toAccountRegistration',
      },
      {
        label: content?.accountRegistrationLabel || 'Account number',
        values: [toAccountDetail.accountNumber],
        evidenceID: 'toAccountNumber',
      },
    );
    if (toAccount.accountData.nickname) {
      toAccountRows.push({
        label: 'Account nickname',
        values: [toAccount.accountData.nickname],
        evidenceID: 'toAccountNickname',
      });
    }
  } else {
    toAccountRows.push(
      {
        label: content?.accountTypeLabel || 'Account type',
        values: ['Individual account'],
        evidenceID: 'toAccountType',
      },
      {
        label: content?.accountRegistrationLabel || 'Account registration',
        values: [name],
        evidenceID: 'toAccountRegistration',
      },
      {
        label: content?.accountNumberLabel || 'Account number',
        values: [
          toAccount?.accountNumber === 'newAccount' ||
          !toAccount?.accountNumber ||
          openAccount.openAccountResponse.isS24Reject
            ? 'Account number pending'
            : toAccount.accountNumber,
        ],
        evidenceID: 'toAccountNumber',
      },
      {
        label: content?.dividendsAndCapitalGainsLabel || 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: ReceiptModal.DIVCAPGAINS,
        evidenceID: 'divAndCap',
      },
      {
        label: content?.settlementFundLabel || 'Settlement fund',
        values: [SettlementFundMap[settlementFund]],
        modal: ReceiptModal.SETTLEMENTFUND,
        evidenceID: 'settlementFund',
        additionalText: getSettlementFundAdditionalText(flowType === FlowTypes.MINOR, content),
      },
    );
  }
  return toAccountRows;
}

function getSettlementFundAdditionalText(
  isMinor: boolean,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
) {
  const settlementFundDescription =
    content?.settlementFundDescription || ReviewContent.settlementFundDescription;
  const fundInvestWarning = content?.fundInvestWarning || ReviewContent.fundInvestWarning;
  return isMinor ? settlementFundDescription : fundInvestWarning;
}

export function buildToEstateAccountRow(
  triageState: TriageState,
  estateState: EstateState,
  decedentName: string,
  editable: boolean,
) {
  const toAccountRows: ReceiptRow[] = [];
  const tin: string = editable
    ? estateState.tin_private!
    : '**-***' + splitClientId(estateState.tin_private!);

  toAccountRows.push(
    {
      label: 'Account type',
      values: ['Individual estate account'],
      evidenceID: 'toAccountType',
    },
    {
      label: 'Estate name',
      values: [`Estate of ${decedentName}`],
      evidenceID: 'toAccountRegistration',
    },
    {
      label: 'Estate tax identification number',
      values: [tin],
      evidenceID: 'toAccountNumber',
    },
  );

  if (triageState.flowType === FlowTypes.INHERITANCE) {
    toAccountRows.push(
      {
        label: 'Dividends & capital gains',
        values: ['Reinvest distributions'],
        modal: ReceiptModal.DIVCAPGAINS,
        evidenceID: 'divAndCap',
      },
      {
        label: 'Settlement fund',
        values: ['Vanguard Federal Money Market Fund'],
        modal: ReceiptModal.SETTLEMENTFUND,
        evidenceID: 'settlementFund',
        additionalText: ReviewContent.fundInvestWarning,
      },
    );
  }

  return toAccountRows;
}

export function buildAccountToNonretirement(
  personalInfo: IPersonalInfoClientState['clientData'],
  selectedAccounts: ISelectedClientAccountsState,
  triageState: TriageState,
  estateState: EstateState,
  decedentName: string,
  openAccountState: IOpenAccountState,
  editable: boolean,
  settlementFund?: SettlementFundAoAEnum | null,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
): IReceipt {
  const flow = urlMapper[triageState.flowType];
  const individualEstate =
    triageState.flowType === FlowTypes.INHERITANCE &&
    triageState.individualDesignation === IndividualDesignationListedTypes.EXECUTOR;
  const editRoute = individualEstate ? `secure/${flow}/estate` : `secure/${flow}/transfer-to`;

  const toAccountReceipt: IReceipt = {
    header: 'Transferring to',
    description: null,
    editable: editable,
    editRoute: editRoute,
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  toAccountReceipt.rows = individualEstate
    ? buildToEstateAccountRow(triageState, estateState, decedentName, editable)
    : buildToAccountRow(
        personalInfo,
        selectedAccounts,
        openAccountState,
        settlementFund ?? SettlementFundAoAEnum.FED,
        triageState.flowType,
        content,
      );
  return toAccountReceipt;
}

export function buildAccountToMultiInheritance(
  currentTransaction: TransactionState | null,
  settlementFund: SettlementFundAoAEnum = SettlementFundAoAEnum.FED,
  content?: typeof ReviewContent.receiptContent.transferFromAndTo,
): IReceipt {
  const toAccountReceipt: IReceipt = {
    header: 'Account',
    description: null,
    editable: true,
    editRoute: 'secure/multi-inheritance/account-triage',
    sectionEvidenceID: 'accountTo',
    rows: [],
  };

  toAccountReceipt.rows = [
    {
      label: content?.accountTypeLabel || 'Account type',
      values: [
        !!currentTransaction && !!currentTransaction.selectedClientAccounts.transferTo
          ? getMultiInheritanceAccountToType(
              currentTransaction.accountTriage.accountTypeInheriting,
              currentTransaction.selectedClientAccounts.transferTo.iraType ?? IRATypes.UNKNOWN,
            )
          : 'Unknown',
      ],
      evidenceID: 'toAccountType',
    },
    {
      label: content?.accountNumberLabel || 'Account number',
      values: ['Account number pending'],
      evidenceID: 'toAccountNumber',
    },
    {
      label: content?.dividendsAndCapitalGainsLabel || 'Dividends & capital gains',
      values: ['Reinvest distributions'],
      modal: ReceiptModal.DIVCAPGAINS,
      evidenceID: 'divAndCap',
    },
    {
      label: 'Source of Funds',
      values: ['Gift/Inheritance'],
      evidenceID: 'sourceOfFunds',
    },
    {
      label: content?.settlementFundLabel || 'Settlement fund',
      values: [SettlementFundMap[settlementFund]],
      modal: ReceiptModal.SETTLEMENTFUND,
      evidenceID: 'settlementFund',
      additionalText: getSettlementFundAdditionalText(false, content),
    },
  ];
  return toAccountReceipt;
}

export function accountName(account: ISelectedAccount) {
  const name = account.accountData.accountName;
  const isBrokerage = account.accountData.productType.includes('BROKERAGE');
  const registration = isBrokerage ? name.substr(0, name.indexOf('—') - 1) : name;

  return registration.replace(',', ' &');
}
